import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import Area from '../../../../domain/entities/Area';
import ExploreArea from '../../../../domain/entities/ExploreArea';
import ExploreStation from '../../../../domain/entities/ExploreStation';
import MerchantCategory from '../../../../domain/entities/MerchantCategory';
import Station from '../../../../domain/entities/Station';
import Button, { ButtonSize } from '../../../common/buttons/Button';
import ExploreSearchField from './ExploreSearchField';
import ExploreSearchInputValue from './ExploreSearchInputValue';
import css from './ExploreSearchModule.module.scss';
import ExploreSuggestAreas from './ExploreSuggestAreas';
import ExploreSuggestCategories from './ExploreSuggestCategories';
import ExploreSuggestPanel from './ExploreSuggestPanel';

export interface Props {
  fieldData: {
    categories?: MerchantCategory[];
    exAreas?: ExploreArea[];
    exStations?: ExploreStation[];
  };
  defaultCategory?: MerchantCategory;
  defaultArea?: Area | Station;
  actions: {
    searchAreas: (query: string, merchantCategoryId: number) => void;
    searchCategories: () => void;
  };
}

interface States {
  categoryField: {
    isActive?: boolean;
    selectedCategory?: MerchantCategory;
  };
  areaField: {
    isActive?: boolean;
    selectedEntity?: Area | Station;
    suggestHeading?: string;
  };
}

class ExploreSearchModule extends React.Component<
  Props & InjectedIntlProps,
  States
> {
  private intlPref = 'components.explore.exploreSearchModule.';

  public static defaultProps: Props = {
    fieldData: {
      categories: [],
      exAreas: [],
      exStations: [],
    },
    actions: {
      searchAreas: (_: string) => undefined,
      searchCategories: () => undefined,
    },
  };

  public state: Readonly<States> = {
    categoryField: {
      isActive: false,
      selectedCategory: undefined,
    },
    areaField: {
      isActive: false,
      selectedEntity: undefined,
      suggestHeading: '',
    },
  };

  constructor(props: Props & InjectedIntlProps) {
    super(props);

    this.state.areaField.selectedEntity = props.defaultArea;
    this.state.categoryField.selectedCategory = props.defaultCategory;
  }

  private toggleCategoryField = (flag: boolean) => {
    this.setState({
      categoryField: { ...this.state.categoryField, ...{ isActive: flag } },
      areaField: { ...this.state.areaField, ...{ isActive: false } },
    });
  };

  private setSelectedCategory = (cat?: MerchantCategory) => {
    this.setState({
      categoryField: {
        ...this.state.categoryField,
        ...{ selectedCategory: cat, isActive: false },
      },
    });
  };

  private toggleAreaField = (flag: boolean) => {
    if (flag && this.state.categoryField.selectedCategory) {
      this.searchAreas('');
    }

    this.setState({
      categoryField: { ...this.state.categoryField, ...{ isActive: false } },
      areaField: { ...this.state.areaField, ...{ isActive: flag } },
    });
  };

  private setSelectedEntity = (entity?: Area | Station) => {
    this.setState({
      areaField: {
        ...this.state.areaField,
        ...{ selectedEntity: entity, isActive: false },
      },
    });
  };

  private intlMessage(id: string): string {
    return this.props.intl.formatMessage({
      id: this.intlPref + id,
    });
  }

  private searchAreas = (q: string): void => {
    const { selectedCategory } = this.state.categoryField;

    this.state.areaField.suggestHeading = q ? '検索結果' : '人気のエリア';

    if (selectedCategory) {
      this.props.actions.searchAreas(q, selectedCategory.id);
    }
  };

  private renderSuggestCategoryPanel(): React.ReactNode {
    return (
      <ExploreSuggestPanel
        heading={this.intlMessage('fields.chooseCategory')}
        handleClose={this.toggleCategoryField}
      >
        <ExploreSuggestCategories
          categories={this.props.fieldData.categories}
          handleClick={this.setSelectedCategory}
        />
      </ExploreSuggestPanel>
    );
  }

  private renderSuggestAreaPanel(): React.ReactNode {
    return (
      <ExploreSuggestPanel
        heading={this.intlMessage('fields.chooseArea')}
        handleClose={this.toggleAreaField}
      >
        <ExploreSuggestAreas
          exAreas={this.props.fieldData.exAreas}
          exStations={this.props.fieldData.exStations}
          handleClick={this.setSelectedEntity}
          handleInput={this.searchAreas}
          heading={this.state.areaField.suggestHeading}
        />
      </ExploreSuggestPanel>
    );
  }

  private makeSubmitPath(): string {
    if (this.state.categoryField.selectedCategory === undefined) {
      return '';
    }

    const path = `/categories/${this.state.categoryField.selectedCategory.id}`;

    if (this.state.areaField.selectedEntity === undefined) {
      return path;
    }

    if ('localName' in this.state.areaField.selectedEntity) {
      return `${path}/areas/${this.state.areaField.selectedEntity.id}`;
    }

    if ('name' in this.state.areaField.selectedEntity) {
      return `${path}/stations/${this.state.areaField.selectedEntity.originalStationId}`;
    }

    return path;
  }

  private selectedEntityValue(entity: Area | Station) {
    if ('localName' in entity) {
      return entity.localName;
    }

    if ('name' in entity) {
      return entity.name + '駅';
    }

    return '';
  }

  public render() {
    return (
      <div className={css.ExploreSearchModule}>
        <div className={css.ExploreSearchModule__Fields}>
          <ExploreSearchField
            placeholder={this.intlMessage('fields.chooseCategory')}
            hasPlaceholder={
              this.state.categoryField.selectedCategory === undefined
            }
            isActive={this.state.categoryField.isActive}
            isDisabled={this.state.categoryField.selectedCategory !== undefined}
            activate={this.toggleCategoryField}
            panel={this.renderSuggestCategoryPanel()}
          >
            {this.state.categoryField.selectedCategory !== undefined && (
              <ExploreSearchInputValue
                value={this.state.categoryField.selectedCategory.nameJA}
                handleDelete={() => this.setSelectedCategory()}
              />
            )}
          </ExploreSearchField>

          <ExploreSearchField
            placeholder={this.intlMessage('fields.chooseArea')}
            hasPlaceholder={this.state.areaField.selectedEntity === undefined}
            isActive={this.state.areaField.isActive}
            isDisabled={
              this.state.areaField.selectedEntity !== undefined ||
              this.state.categoryField.selectedCategory === undefined
            }
            activate={this.toggleAreaField}
            panel={this.renderSuggestAreaPanel()}
            handleInput={this.searchAreas}
          >
            {this.state.areaField.selectedEntity !== undefined && (
              <ExploreSearchInputValue
                value={this.selectedEntityValue(
                  this.state.areaField.selectedEntity,
                )}
                handleDelete={() => this.setSelectedEntity()}
              />
            )}
          </ExploreSearchField>
        </div>

        <Button
          buttonSize={ButtonSize.XLarge}
          className={css.ExploreSearchModule__Button}
          disabled={this.state.categoryField.selectedCategory === undefined}
          href={this.makeSubmitPath()}
        >
          {this.intlMessage('buttons.searchService')}
        </Button>
      </div>
    );
  }
}

export default injectIntl(ExploreSearchModule);
